"use client";

import type { ReactNode } from "react";
import { useGlobalPlayer } from "@/context/global-player-context";
import { cn } from "@/lib/utils";

/**
 * The global player in the editorial shell: "Now playing — Title" with Pause /
 * Play, in the bottom-left corner and the phone bottom band. Playback itself
 * stays in `GlobalPlayerSheet`, whose dock is hidden inside `.ed-root`.
 */
export function NowPlaying({
	fallback = null,
	className,
}: {
	/** Shown when nothing is playing (e.g. the page action on phones). */
	fallback?: ReactNode;
	className?: string;
}) {
	const { submission, isOpen, isPlaying, pausePlayer, playPlayer } =
		useGlobalPlayer();

	if (!submission || !isOpen) return <>{fallback}</>;

	const title = submission.track_name?.trim() || "Untitled";

	return (
		<section
			aria-label="Now playing"
			className={cn("ed-now-playing ed-ui", className)}
		>
			<span aria-hidden className="ed-now-playing__eq" data-playing={isPlaying}>
				<span />
				<span />
				<span />
			</span>
			<span className="ed-now-playing__title">Now playing — {title}</span>
			<button
				type="button"
				className="ed-button-text ed-ui ed-link"
				onClick={isPlaying ? pausePlayer : playPlayer}
				aria-label={`${isPlaying ? "Pause" : "Play"} ${title}`}
			>
				{isPlaying ? "Pause" : "Play"}
			</button>
		</section>
	);
}
